import {
    Dispatch,
    FocusEvent,
    RefObject,
    SetStateAction,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';

/** How long a picked choice waits before it saves, so a few quick clicks save once. */
export const CHOICE_SAVE_DELAY_MS = 500;

export const INCOMPLETE_DATE = 'A date is only partly filled in. Complete or clear it.';

/** Whether a date in `root` is typed only in part: its value reads empty, so saving it would clear the stored date. */
export const hasIncompleteDate = (root: HTMLElement | null) =>
    Array.from(root?.querySelectorAll<HTMLInputElement>('input[type="date"]') ?? []).some(
        (input) => input.validity.badInput,
    );

/** A field the user types into saves once they leave it, not while they type. */
const isTypingField = (element: Element | null) =>
    element instanceof HTMLTextAreaElement ||
    (element instanceof HTMLInputElement && !['checkbox', 'radio', 'button', 'submit', 'reset'].includes(element.type));

/**
 * - `idle`: nothing changed since the values were loaded
 * - `editing`: changed, saving once the user leaves the field
 * - `saving`: a save runs
 * - `saved`: everything changed is saved
 * - `blocked`: changed, but a check refused the values; `blocker` says why
 * - `failed`: changed, and the last save failed
 */
export type AutosaveStatus = 'idle' | 'editing' | 'saving' | 'saved' | 'blocked' | 'failed';

type Phase = Exclude<AutosaveStatus, 'editing'>;

/** A finished phase stands only while nothing changed since, except a refusal or a failure, which the user must act on. */
const statusOf = (phase: Phase, dirty: boolean): AutosaveStatus => {
    switch (phase) {
        case 'saving':
            return 'saving';
        case 'blocked':
        case 'failed':
            return dirty ? phase : 'idle';
        case 'saved':
            return dirty ? 'editing' : 'saved';
        default:
            return dirty ? 'editing' : 'idle';
    }
};

export interface Autosave {
    status: AutosaveStatus;
    blocker?: string;
    /** Saves now unless nothing changed or a check refuses; also retries a failed save. */
    flush: () => void;
    /** For the element around the fields. */
    bodyProps: { ref: RefObject<HTMLDivElement>; onFocus: () => void; onBlur: (event: FocusEvent) => void };
}

interface Saved<T> {
    values: T;
    json: string;
}

/**
 * Form values that save themselves: a typed field once focus leaves it, a choice shortly after it is picked, and
 * whatever is left when the form goes away. One save runs at a time; changes made meanwhile follow it.
 *
 * The form takes up `stored` again whenever it changes while the form holds nothing unsaved and focus is outside the
 * fields, so the user's own typing is never replaced under them.
 */
export const useAutosave = <T>({
    stored,
    save,
    check,
}: {
    /** The values as stored now. */
    stored: T;
    /** Persists `values`; `saved` are the values last saved or loaded, to tell what changed. */
    save: (values: T, saved: T) => Promise<unknown>;
    /** Why `values` cannot be saved, or `undefined` when they can; run at each save attempt, it may also mark the fields. */
    check?: (values: T) => string | undefined;
}): { values: T; setValues: Dispatch<SetStateAction<T>>; autosave: Autosave } => {
    const storedJSON = JSON.stringify(stored);
    const [values, setValues] = useState(stored);
    const [savedJSON, setSavedJSON] = useState(storedJSON);
    const [phase, setPhase] = useState<{ status: Phase; blocker?: string }>({ status: 'idle' });
    const [focused, setFocused] = useState(false);
    const json = JSON.stringify(values);
    const dirty = json !== savedJSON;

    const body = useRef<HTMLDivElement>(null);
    const saved = useRef<Saved<T>>({ values: stored, json: storedJSON });
    const syncedJSON = useRef(storedJSON);
    const saving = useRef(false);
    const again = useRef(false);
    // For a save after the fields are gone, when the date inputs can no longer be asked
    const incompleteDate = useRef(false);
    const latest = useRef({ values, save, check });
    useLayoutEffect(() => {
        latest.current = { values, save, check };
    });

    const attempt = useCallback(() => {
        if (saving.current) {
            again.current = true;
            return;
        }
        const { values: current, save: persist, check: refuse } = latest.current;
        const currentJSON = JSON.stringify(current);
        if (currentJSON === saved.current.json) return;
        if (body.current) incompleteDate.current = hasIncompleteDate(body.current);
        const blocker = incompleteDate.current ? INCOMPLETE_DATE : refuse?.(current);
        if (blocker) {
            setPhase({ status: 'blocked', blocker });
            return;
        }
        saving.current = true;
        again.current = false;
        setPhase({ status: 'saving' });
        Promise.resolve()
            .then(() => persist(current, saved.current.values))
            .then(
                () => {
                    saved.current = { values: current, json: currentJSON };
                    setSavedJSON(currentJSON);
                    return 'saved' as const;
                },
                () => 'failed' as const,
            )
            // Cleared before the render the phase starts, which looks for stored changes to take up
            .then((outcome) => {
                saving.current = false;
                setPhase({ status: outcome });
            });
    }, []);

    // A choice saves after a pause; a typed field once the user leaves it
    useEffect(() => {
        if (body.current) incompleteDate.current = hasIncompleteDate(body.current);
        if (!dirty) return undefined;
        const timer = setTimeout(() => {
            const active = document.activeElement;
            if (!(isTypingField(active) && body.current?.contains(active))) attempt();
        }, CHOICE_SAVE_DELAY_MS);
        return () => clearTimeout(timer);
    }, [json, dirty, attempt]);

    /**
     * A save asked for during another follows it after a pause, even while the user types in the next field: the stored
     * values that one brought back reach the form through its parent's render, and `save` diffs against them.
     */
    useEffect(() => {
        if (phase.status === 'saving' || !again.current) return undefined;
        const timer = setTimeout(attempt, CHOICE_SAVE_DELAY_MS);
        return () => clearTimeout(timer);
    }, [phase, attempt]);

    // `stored` is new with every render, so this looks again after each: stored values, focus and edits all decide
    useEffect(() => {
        if (storedJSON === syncedJSON.current || dirty || saving.current) return;
        // The fields can go away with focus still in them, which sends no blur
        if (focused && body.current?.contains(document.activeElement)) return;
        syncedJSON.current = storedJSON;
        saved.current = { values: stored, json: storedJSON };
        setValues(stored);
        setSavedJSON(storedJSON);
    }, [stored, storedJSON, dirty, focused]);

    useEffect(() => () => attempt(), [attempt]);

    const unsaved = dirty || phase.status === 'saving';
    useEffect(() => {
        if (!unsaved) return undefined;
        const warn = (event: BeforeUnloadEvent) => event.preventDefault();
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [unsaved]);

    const onBlur = (event: FocusEvent) => {
        attempt();
        if (!body.current?.contains(event.relatedTarget as Node | null)) setFocused(false);
    };

    const status = statusOf(phase.status, dirty);

    return {
        values,
        setValues,
        autosave: {
            status,
            blocker: status === 'blocked' ? phase.blocker : undefined,
            flush: attempt,
            bodyProps: { ref: body, onFocus: () => setFocused(true), onBlur },
        },
    };
};
